function noop() {}

/** Same env the app already uses when it writes cookies. Not a separate host list. */
export function configuredAuthCookieDomain() {
  return process.env.NEXT_PUBLIC_COOKIE_DOMAIN || process.env.VITE_COOKIE_DOMAIN || ''
}

/**
 * `userAuth` sets `cfauth_uid` on the configured cookie domain. A page can read
 * that cookie only when its host is that domain or a subdomain of it.
 */
export function hostCanReadAuthCookie(hostname: string, cookieDomain: string) {
  const domain = cookieDomain.trim().replace(/^\./, '').toLowerCase()
  if (!domain) {
    return false
  }
  const host = hostname.trim().toLowerCase()
  return host === domain || host.endsWith(`.${domain}`)
}

/**
 * Call `markUserAuthSettled` after the userAuth response. When this host cannot
 * read the cookie, waiting any longer would serialize every GraphQL request for
 * the session, so the gate opens. When the host can read it, a missing cookie
 * still means the identity has not settled.
 */
export function createIdentitySignal({
  hasDocumentIdentity,
  canObserveIdentityCookie,
}: {
  hasDocumentIdentity: () => boolean
  canObserveIdentityCookie: () => boolean
}) {
  let userAuthSettled = false

  return {
    markUserAuthSettled() {
      userAuthSettled = true
    },
    hasIdentity() {
      if (hasDocumentIdentity()) {
        return true
      }
      return userAuthSettled && !canObserveIdentityCookie()
    },
  }
}

/**
 * The gateway mints a fresh anonymous user ID for every request that arrives without one, and the
 * browser keeps whichever Set-Cookie lands last. Parallel first-visit requests would each get a
 * different ID, so work done under one ID is orphaned when sign-in migrates another. Until an
 * identity exists, one request runs alone; the rest wait for it to finish so cookies (and any
 * registered token persistence) are in place before they go out.
 */
export function createSettledIdentityGate(hasIdentity: () => boolean) {
  let leader: Promise<void> | undefined

  return async function send<T>(request: () => Promise<T>): Promise<T> {
    while (!hasIdentity()) {
      if (leader) {
        await leader
        continue
      }

      // Derived from the request promise rather than Promise.withResolvers: the
      // first requests of a boot fire before the app's polyfills have loaded.
      const attempt = request()
      leader = attempt.then(noop, noop).finally(() => {
        leader = undefined
      })

      return attempt
    }

    return request()
  }
}
